import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueries } from '@tanstack/react-query'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
    Clock,
    CheckCircle,
    XCircle,
    ExternalLink,
    ChevronDown,
    ChevronUp,
    Server,
} from 'lucide-react'
import { Typogaphy } from 'vnetwork-ui'
import { Link } from 'react-router-dom'

import Paper from '@/components/Paper'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { getServiceDowntimeLogs } from '@/modules/Service/services/sla'
import {
    SLADowntimeLog,
    DowntimeType,
    DowntimeCause,
    DOWNTIME_TYPE_LABELS,
    DOWNTIME_CAUSE_LABELS,
} from '@/modules/Service/types/sla'
import { formatFromUTC } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'

dayjs.extend(utc)

interface ServiceInfo {
    service_id: number
    service_name?: string
}

interface CustomerDowntimeLogsProps {
    customerId: number
    startDate: string
    endDate: string
    services: ServiceInfo[]
}

// Extended type with service info
interface AggregatedDowntimeLog extends SLADowntimeLog {
    service_name?: string
}

// Gray-based badge styles
const getDowntimeTypeBadge = (_type: DowntimeType) => {
    return 'bg-gray-100 text-gray-600 border-gray-200'
}

const getDowntimeCauseBadge = (cause: DowntimeCause) => {
    // Highlight provider (our fault) in red
    if (cause === 'provider') {
        return 'bg-red-50 text-red-600 border-red-200'
    }
    return 'bg-gray-100 text-gray-500 border-gray-200'
}

const formatDuration = (minutes?: number) => {
    if (!minutes) return '-'
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours > 0) {
        return `${hours}h ${mins}m`
    }
    return `${mins}m`
}

const CustomerDowntimeLogs: React.FC<CustomerDowntimeLogsProps> = ({
    startDate,
    endDate,
    services,
}) => {
    const { t } = useTranslation('services')
    const [isExpanded, setIsExpanded] = useState(false)

    // Fetch downtime logs for all services (parallel)
    const downtimeQueries = useQueries({
        queries: services.map((service: ServiceInfo) => ({
            queryKey: [
                'service-downtime',
                service.service_id,
                startDate,
                endDate,
            ],
            queryFn: () =>
                getServiceDowntimeLogs(service.service_id, {
                    page: 1,
                    page_size: 50,
                    q: '',
                }),
            enabled: !!service.service_id && services.length > 0,
            staleTime: 5 * 60 * 1000, // 5 minutes
        })),
    })

    // Check if all downtime queries are done
    const isLoading = downtimeQueries.some((q) => q.isLoading)

    // Aggregate all downtime logs with service names
    const aggregatedLogs = useMemo(() => {
        const logs: AggregatedDowntimeLog[] = []

        downtimeQueries.forEach((query, index) => {
            if (query.data?.results) {
                const service = services[index]
                query.data.results.forEach((log: SLADowntimeLog) => {
                    logs.push({
                        ...log,
                        service_name: service?.service_name,
                    })
                })
            }
        })

        // Sort by downtime_start desc (most recent first)
        return logs.sort(
            (a, b) =>
                dayjs(b.downtime_start).valueOf() -
                dayjs(a.downtime_start).valueOf()
        )
    }, [downtimeQueries, services])

    const isOngoing = (log: AggregatedDowntimeLog) => !log.downtime_end

    // Calculate summary stats
    const summaryStats = useMemo(() => {
        return {
            provider: aggregatedLogs.filter(
                (l) => l.downtime_cause === 'provider'
            ).length,
            customer: aggregatedLogs.filter(
                (l) => l.downtime_cause === 'customer'
            ).length,
            third_party: aggregatedLogs.filter(
                (l) => l.downtime_cause === 'third_party'
            ).length,
            ongoing: aggregatedLogs.filter((l) => isOngoing(l)).length,
        }
    }, [aggregatedLogs])

    if (isLoading) {
        return (
            <Paper className="p-4">
                <div className="flex items-center gap-2 mb-3">
                    <Skeleton className="h-5 w-5" />
                    <Skeleton className="h-5 w-32" />
                </div>
                <Skeleton className="h-20 rounded-lg" />
            </Paper>
        )
    }

    // No services or no downtime logs - show empty state
    if (services.length === 0 || aggregatedLogs.length === 0) {
        return (
            <Paper className="p-4">
                <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-gray-600">
                        {t(
                            'sla.no_downtime_in_period',
                            'Không có downtime trong khoảng thời gian này'
                        )}
                    </span>
                </div>
            </Paper>
        )
    }

    return (
        <Paper className="p-4">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <Typogaphy className="font-medium text-gray-700 text-sm">
                        {t('sla.downtime_history', 'Lịch sử Downtime')}
                    </Typogaphy>
                    <span className="text-xs text-gray-400">
                        ({aggregatedLogs.length})
                    </span>
                    {summaryStats.ongoing > 0 && (
                        <span className="text-xs text-gray-600 animate-pulse">
                            • {summaryStats.ongoing} đang diễn ra
                        </span>
                    )}
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="h-7 text-xs text-gray-500"
                >
                    {isExpanded ? (
                        <>
                            <ChevronUp className="w-3 h-3 mr-1" />
                            Thu gọn
                        </>
                    ) : (
                        <>
                            <ChevronDown className="w-3 h-3 mr-1" />
                            Chi tiết
                        </>
                    )}
                </Button>
            </div>

            {!isExpanded ? (
                // Compact view - inline summary
                <div className="flex flex-wrap items-center gap-4 text-sm">
                    <div className="flex items-center gap-1.5">
                        <span className="text-gray-500">Nội bộ:</span>
                        <span
                            className={`font-semibold ${summaryStats.provider > 0 ? 'text-red-600' : 'text-gray-700'}`}
                        >
                            {summaryStats.provider}
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="text-gray-500">KH:</span>
                        <span className="font-semibold text-gray-700">
                            {summaryStats.customer}
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="text-gray-500">Bên 3:</span>
                        <span className="font-semibold text-gray-700">
                            {summaryStats.third_party}
                        </span>
                    </div>
                </div>
            ) : (
                // Expanded view - show full table
                <Table className="text-xs">
                    <TableHeader>
                        <TableRow className="h-8">
                            <TableHead className="py-2 px-2 text-xs">
                                <div className="flex items-center gap-1">
                                    <Server className="w-3 h-3" />
                                    {t('service', 'Dịch vụ')}
                                </div>
                            </TableHead>
                            <TableHead className="py-2 px-2 text-xs">
                                {t('sla.type', 'Loại')}
                            </TableHead>
                            <TableHead className="py-2 px-2 text-xs">
                                {t('sla.cause', 'Nguyên nhân')}
                            </TableHead>
                            <TableHead className="py-2 px-2 text-xs">
                                {t('sla.start', 'Bắt đầu')}
                            </TableHead>
                            <TableHead className="py-2 px-2 text-xs">
                                {t('sla.end', 'Kết thúc')}
                            </TableHead>
                            <TableHead className="py-2 px-2 text-xs">
                                {t('sla.duration', 'Thời lượng')}
                            </TableHead>
                            <TableHead className="py-2 px-2 text-xs text-center">
                                {t('sla.counts_sla', 'Tính SLA')}
                            </TableHead>
                            <TableHead className="py-2 px-2 text-xs">
                                {t('sla.description', 'Mô tả')}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {aggregatedLogs.map((log) => (
                            <TableRow key={log.downtime_log_id} className="h-9">
                                <TableCell className="py-1.5 px-2">
                                    <Link
                                        to={`${generatePathDatacentral(paths.serviceDetail, { id: log.service_id })}?tab=sla`}
                                        target="_blank"
                                        className="flex items-center gap-1 text-gray-600 hover:text-gray-800 hover:underline"
                                    >
                                        <span className="truncate max-w-[120px]">
                                            {log.service_name ||
                                                `#${log.service_id}`}
                                        </span>
                                        <ExternalLink className="w-3 h-3 shrink-0" />
                                    </Link>
                                </TableCell>
                                <TableCell className="py-1.5 px-2">
                                    <Badge
                                        variant="outline"
                                        className={`text-[10px] px-1.5 py-0.5 ${getDowntimeTypeBadge(
                                            log.downtime_type
                                        )}`}
                                    >
                                        {
                                            DOWNTIME_TYPE_LABELS[
                                                log.downtime_type
                                            ]
                                        }
                                    </Badge>
                                </TableCell>
                                <TableCell className="py-1.5 px-2">
                                    {log.downtime_cause && (
                                        <Badge
                                            variant="outline"
                                            className={`text-[10px] px-1.5 py-0.5 ${getDowntimeCauseBadge(
                                                log.downtime_cause
                                            )}`}
                                        >
                                            {
                                                DOWNTIME_CAUSE_LABELS[
                                                    log.downtime_cause
                                                ]
                                            }
                                        </Badge>
                                    )}
                                </TableCell>
                                <TableCell className="py-1.5 px-2 text-xs text-gray-600">
                                    {formatFromUTC(log.downtime_start)}
                                </TableCell>
                                <TableCell className="py-1.5 px-2 text-xs">
                                    {isOngoing(log) ? (
                                        <span className="text-gray-500 animate-pulse">
                                            đang diễn ra
                                        </span>
                                    ) : (
                                        <span className="text-gray-600">
                                            {formatFromUTC(log.downtime_end)}
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell className="py-1.5 px-2 font-medium text-xs">
                                    {isOngoing(log) ? (
                                        <span className="text-gray-700">
                                            {formatDuration(
                                                dayjs().diff(
                                                    dayjs.utc(
                                                        log.downtime_start
                                                    ),
                                                    'minute'
                                                )
                                            )}
                                        </span>
                                    ) : (
                                        formatDuration(log.downtime_minutes)
                                    )}
                                </TableCell>
                                <TableCell className="py-1.5 px-2 text-center">
                                    {log.counts_against_sla ? (
                                        <XCircle className="w-3.5 h-3.5 text-gray-500 mx-auto" />
                                    ) : (
                                        <CheckCircle className="w-3.5 h-3.5 text-gray-400 mx-auto" />
                                    )}
                                </TableCell>
                                <TableCell className="py-1.5 px-2 text-xs text-gray-500 max-w-[150px] truncate">
                                    {log.downtime_description || '-'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Paper>
    )
}

export default CustomerDowntimeLogs
